import { useCallback, useEffect, useRef, useState } from "react";
import { api, mesajEroare } from "./api.js";
import { tr, trMesajServer } from "./i18n.js";

/**
 * Textele evenimentelor se scriu o singură dată, aici. Enumerarea brută
 * („SlaAvertizare75") n-are ce căuta pe ecranul nimănui.
 */
const EVENIMENTE = {
  TichetDeschis: "Tichet nou deschis",
  TichetPreluat: "Tichet preluat de un dispecer",
  VizitaPropusa: "Vizită propusă clientului",
  VizitaAcceptata: "Vizită acceptată",
  VizitaRefuzata: "Vizită refuzată",
  OfertaAcceptata: "Ofertă acceptată de client",
  OfertaRefuzata: "Ofertă refuzată de client",
  TehnicianPlecat: "Tehnicianul a plecat spre client",
  InterventieFinalizata: "Intervenție finalizată",
  FisaSemnata: "Fișă semnată de client",
  SlaAvertizare75: "SLA — 75% din termen consumat",
  SlaAvertizare90: "SLA — 90% din termen consumat",
  SlaDepasit: "SLA depășit",
  MentenantaScadenta: "Mentenanță preventivă scadentă",
  GarantieExpira: "Garanție care expiră",
  CalificareExpira: "Calificare de tehnician care expiră",
  StocSubPrag: "Stoc sub pragul de alarmă",
  DevizAsteaptaAprobare: "Deviz sau ofertă de aprobat",
};

const cand = (v) => {
  const d = new Date(v);
  const min = Math.round((Date.now() - d.getTime()) / 60000);
  if (min < 1) return "acum";
  if (min < 60) return `acum ${min} min`;
  if (min < 1440) return `acum ${Math.floor(min / 60)} h`;
  return d.toLocaleDateString("ro-RO", { day: "2-digit", month: "2-digit", year: "numeric" });
};

/** Clopoțelul din bara de sus: numărul necitit + panoul care se deschide sub el. */
export default function Notificari({ laDeschidereEntitate }) {
  const [numar, setNumar] = useState(0);
  const [deschis, setDeschis] = useState(false);
  const [lista, setLista] = useState([]);
  const [preferinte, setPreferinte] = useState(null);
  const [eroare, setEroare] = useState("");
  const zona = useRef(null);

  const numaraDinNou = useCallback(() => {
    api.numarNotificari().then((r) => setNumar(r.necitite ?? 0)).catch(() => {});
  }, []);

  // Un interval, nu un canal deschis permanent: aplicația e una de birou, iar
  // un minut întârziere la un clopoțel nu schimbă nimic operațional.
  useEffect(() => {
    numaraDinNou();
    const t = setInterval(numaraDinNou, 60_000);
    return () => clearInterval(t);
  }, [numaraDinNou]);

  useEffect(() => {
    if (!deschis) return;
    api.notificari().then(setLista).catch((e) => setEroare(mesajEroare(e)));
    const inafara = (e) => { if (zona.current && !zona.current.contains(e.target)) setDeschis(false); };
    document.addEventListener("mousedown", inafara);
    return () => document.removeEventListener("mousedown", inafara);
  }, [deschis]);

  async function citeste(n) {
    if (!n.citita) {
      try { await api.citesteNotificarea(n.id); } catch { /* deja citită */ }
      setLista((l) => l.map((x) => (x.id === n.id ? { ...x, citita: true } : x)));
      numaraDinNou();
    }
    if (n.entitate && n.entitateId) laDeschidereEntitate?.(n.entitate, n.entitateId);
  }

  return (
    <div className="clopotel" ref={zona}>
      <button className="buton buton-mic buton-sters clopotel-buton"
        style={{ color: "#cfe0da", borderColor: "#2a3a35" }}
        onClick={() => setDeschis((d) => !d)}
        title={numar ? tr("{0} notificări necitite", numar) : tr("Notificări")}>
        {tr("anunțuri")}
        {numar > 0 && <span className="clopotel-numar">{numar > 99 ? "99+" : numar}</span>}
      </button>

      {deschis && (
        <div className="clopotel-panou">
          <div className="clopotel-antet">
            <strong>{tr("Anunțuri")}</strong>
            <div className="actiuni-catalog">
              <button className="buton buton-mic buton-discret"
                onClick={() => setPreferinte((p) => (p ? null : "se-incarca"))}>
                {preferinte ? tr("înapoi la listă") : tr("ce vreau să primesc")}
              </button>
              {!preferinte && (
                <button className="buton buton-mic buton-discret" disabled={!numar}
                  onClick={async () => {
                    await api.citesteToateNotificarile();
                    setLista((l) => l.map((x) => ({ ...x, citita: true })));
                    numaraDinNou();
                  }}>
                  {tr("marchează tot citit")}
                </button>
              )}
            </div>
          </div>

          {eroare && <div className="eroare">{eroare}</div>}

          {preferinte ? (
            <Preferinte laEroare={setEroare} />
          ) : lista.length === 0 ? (
            <div className="gol" style={{ padding: "1.5rem 1rem" }}>{tr("Niciun anunț.")}</div>
          ) : (
            <ul className="clopotel-lista">
              {lista.map((n) => (
                <li key={n.id} className={n.citita ? "" : "necitita"}>
                  <button onClick={() => citeste(n)}>
                    <div className="clopotel-titlu">{trMesajServer(n.titlu)}</div>
                    {/* Titlul și textul sunt scrise de server ȘI PĂSTRATE în bază:
                        o notificare scrisă ieri rămâne în română, deci se traduce
                        ca TIPAR, nu prin căutare exactă. */}
                    <div className="clopotel-text">{trMesajServer(n.text)}</div>
                    <div className="fara-pret">
                      {tr(EVENIMENTE[n.eveniment] ?? n.eveniment)} · {cand(n.createdAt)}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

function Preferinte({ laEroare }) {
  const [rows, setRows] = useState(null);

  // Serverul spune dacă poate trimite email. Un comutator care promite ceva ce
  // serverul nu face e mai rău decât unul lipsă — de-aia se întreabă, nu se
  // presupune. Dacă întrebarea eșuează, se rămâne pe „nu poate": mai puțin
  // promis decât e adevărat, niciodată mai mult.
  const [emailPornit, setEmailPornit] = useState(false);

  useEffect(() => {
    api.preferinteNotificari().then(setRows).catch((e) => laEroare(mesajEroare(e)));
    api.stareEmail().then((r) => setEmailPornit(!!r.configurat)).catch(() => setEmailPornit(false));
  }, [laEroare]);

  if (!rows) return <div className="gol" style={{ padding: "1.5rem 1rem" }}>{tr("Se încarcă…")}</div>;

  async function comuta(p, camp, valoare) {
    setRows((l) => l.map((x) => (x.eveniment === p.eveniment ? { ...x, [camp]: valoare, implicit: false } : x)));
    try { await api.schimbaPreferinta(p.eveniment, { [camp]: valoare }); }
    catch (e) { laEroare(mesajEroare(e)); }
  }

  return (
    <div className="clopotel-preferinte">
      <p className="nota-cautare">
        {tr("Ce nu e setat explicit e pornit — un anunț pe care nu l-ai oprit tu ajunge la tine.")}
      </p>
      {!emailPornit && (
        <div className="panou panou-atentie"><div className="panou-corp">
          <strong>{tr("Emailul nu e configurat pe acest server.")}</strong>{" "}
          {tr("Anunțurile ajung deocamdată doar aici, în aplicație. Preferința se poate seta oricum — se aplică din clipa în care serverul capătă un server de email.")}
        </div></div>
      )}
      <table className="tabel">
        <thead><tr>
          <th>{tr("Eveniment")}</th><th>{tr("în aplicație")}</th>
          <th>{emailPornit ? tr("pe email") : tr("pe email (indisponibil)")}</th>
        </tr></thead>
        <tbody>
          {rows.map((p) => (
            <tr key={p.eveniment}>
              <td>
                {tr(EVENIMENTE[p.eveniment] ?? p.eveniment)}
                {p.implicit && <span className="marcaj">{tr("implicit")}</span>}
              </td>
              <td>
                <input type="checkbox" checked={!!p.inApp}
                  onChange={(e) => comuta(p, "inApp", e.target.checked)} />
              </td>
              <td>
                <input type="checkbox" checked={!!p.email}
                  onChange={(e) => comuta(p, "email", e.target.checked)}
                  title={emailPornit
                    ? undefined
                    : tr("Serverul n-are încă email configurat; preferința se păstrează.")} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
